/**
 * Shapes of the native Audience / Targeting page (/audience), spec
 * docs/superpowers/specs/2026-10-16-native-audience-targeting-design.md. Types and the page size; nothing else. NULL
 * is "not measurable", never 0, in every number below.
 *
 * @module services/audience/audienceTypes
 */
import type {
  AdChannel, BreakdownMetrics, CampaignState, EntityStatus, NoSpendEmpty, SegmentPoint, TargetingBreakdownRow,
} from '@/services/channels/channelTypes';
import type { BoxState, CurrencyChoice, ObjectiveFilter } from '@/services/overview/overviewTypes';

/** One get_audience_facts row (migration 124), normalised: one ad set and its sums over the period. */
export interface AudienceFact {
  workspaceId: string;
  platform: AdChannel;
  adSetId: string;
  /** Trimmed; a blank one is null. */
  externalAdSetId: string | null;
  /** As stored; a blank one shows the id's first 8 characters (R3). */
  adSetName: string;
  adSetStatus: EntityStatus | null;
  campaignId: string;
  /** As stored ('' when NULL): the Search / Shopping rule reads it (spec §4.2). */
  campaignName: string;
  campaignStatus: EntityStatus | null;
  campaignEndDate: string | null;
  /** 'unspecified' when the campaign has none. */
  objective: string;
  /** Trimmed; null = the account's currency is unknown: the ad set is dropped and counted (spec §4.3). */
  currency: string | null;
  spend: number | null;
  impressions: number | null;
  clicks: number | null;
  /** The ad set's newest day with a metric row in the period (R1). */
  lastDate: string;
}

/** Rows drawn before "แสดงทั้งหมด" (spec §3.2); D4's own, so its page size does not follow D3's (R6). */
export const SHOW_FIRST = 15;

/**
 * One table row (spec §3.4, §4.5). A D2 Targeting row (key = the ad-set id, name, family, the metrics), so D2's
 * segmentsOf takes it as it is; its three engagement fields are null (R5).
 */
export interface AudienceRow extends TargetingBreakdownRow {
  platform: AdChannel;
  externalAdSetId: string | null;
  campaignName: string;
  /** The chip and the CSV's Status (R2, R4). */
  state: CampaignState;
  /** The row's spend ÷ Σ spend over every row (spec §4.5), 0–1; null = "—". */
  share: number | null;
}

/** "รวม": the sums and the rates over every row, and its Share: 1, or null when Σ spend is null or 0. */
export interface AudienceTotal extends BreakdownMetrics {
  share: number | null;
}

/** What the page applies, all on the client (spec §2.2: no read). */
export interface AudienceFilters {
  /** Raw channel ids; [] = every channel. */
  channels: readonly string[];
  objective: ObjectiveFilter;
  /** The requested currency; null = the most spent. */
  currency: string | null;
  activeOnly: boolean;
  showAll: boolean;
}

export interface AudienceTableData {
  /** Every row after the filters and the sort: the CSV writes all of them (R12). */
  rows: AudienceRow[];
  /** What the page draws: the first SHOW_FIRST, or every row when showAll. */
  shown: AudienceRow[];
  /** Over every row, never over the first 15. */
  total: AudienceTotal;
  /** Box 1, over every row (spec §3.3). */
  segments: SegmentPoint[];
  /** Ad sets in the page currency after §4.2, before the channel, objective and Active filters: 0 = the period has none (R9). */
  inCurrency: number;
}

export interface AudienceView {
  /** D1's choice over the ad sets left after §4.2; null until the facts are ready. */
  money: CurrencyChoice | null;
  /**
   * Ad sets left out because their account's currency is unknown (spec §4.3); 0 under either card, which shows no
   * figure (rules-04 H3, I2).
   */
  unknownCurrency: number;
  /** The Google ad groups §4.2 left out (R8); null until the facts are ready. */
  leftOut: number | null;
  /**
   * "ซ่อน N" (R7): the campaigns that are not Active under the other filters; null until the facts are ready, and under
   * either card, which hides every row (rules-04 H review F6, I2).
   */
  hidden: number | null;
  /** The newest lastDate among the facts (R1); null = none. */
  latestFactDate: string | null;
  /**
   * Rule 04.3, as D2's E: no fact of the channels in view spent in the period, Search and Shopping included, checked
   * on every fact before §4.2 and before the objective, currency and Active filters. The page draws D2's card in place
   * of both boxes, and the table has no rows. notConnected when the one ticked channel is known not to be connected
   * (rules-04 G3), and then first, as D2's E: whatever the facts hold, spend included, and while they load or after
   * they failed (rules-04 H3). Else noSpend; null until the facts are ready, and while they spent.
   */
  empty: NoSpendEmpty | null;
  table: BoxState<AudienceTableData>;
}
